import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  GraduationCap, Handshake, Globe, ChartLine, ShieldCheck, Sun,
  BookOpen, ArrowRight, Clock, CircleNotch, Calendar, Sparkle,
} from "@phosphor-icons/react";
import { fetchMarketAcademy } from "@/lib/api";

const ICONS = {
  Handshake, Globe, ChartLine, ShieldCheck, Sun, BookOpen,
};

function LessonCard({ lesson, isOpen, onToggle }) {
  const Icon = ICONS[lesson.icon] || BookOpen;
  return (
    <div
      className="eid-card overflow-hidden"
      data-testid={`lesson-${lesson.id}`}
    >
      <button
        onClick={onToggle}
        data-testid={`lesson-toggle-${lesson.id}`}
        className="w-full text-left p-5 flex items-start gap-3 hover:bg-white/5 transition-colors"
      >
        <span
          className="p-2 rounded-sm shrink-0"
          style={{ backgroundColor: `${lesson.color}20`, color: lesson.color }}
        >
          <Icon size={18} weight="fill" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500">
            <span style={{ color: lesson.color }}>{lesson.level}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-0.5">
              <Clock size={9} /> {lesson.min_reading_min} Min Lesezeit
            </span>
          </div>
          <h3 className="text-lg font-medium tracking-tight mt-0.5">{lesson.title}</h3>
          <p className="text-sm text-zinc-400 mt-0.5">{lesson.tagline}</p>
        </div>
        <ArrowRight
          size={16}
          className={`text-zinc-500 mt-2 transition-transform ${isOpen ? "rotate-90" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pl-[68px]" data-testid={`lesson-content-${lesson.id}`}>
          <div className="space-y-3 text-sm leading-relaxed text-white">
            {(lesson.story || []).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div
            className="mt-4 p-3 rounded-sm border-l-4"
            style={{ borderColor: lesson.color, backgroundColor: `${lesson.color}10` }}
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider" style={{ color: lesson.color }}>
              <Sparkle size={10} weight="fill" /> Merksatz
            </div>
            <p className="text-sm font-medium mt-1">{lesson.takeaway}</p>
          </div>
          {lesson.deep_dive && (
            <details className="mt-3">
              <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-400">
                Tiefer einsteigen
              </summary>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{lesson.deep_dive}</p>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export default function MarketAcademy() {
  const [lessons, setLessons] = useState(null);
  const [rhythms, setRhythms] = useState([]);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    fetchMarketAcademy()
      .then((d) => {
        setLessons(d.lessons || []);
        setRhythms(d.rhythms || []);
      })
      .catch(() => setLessons([]));
  }, []);

  return (
    <div className="space-y-6" data-testid="market-academy-page">
      <Helmet>
        <title>Markt-Akademie · bodenblick.com</title>
      </Helmet>

      <div
        className="eid-card p-6 md:p-10 relative overflow-hidden"
        data-testid="academy-hero"
        style={{
          background:
            "radial-gradient(circle at 90% 10%, #0E1A14 0%, transparent 55%), linear-gradient(135deg, #141414 0%, #181818 100%)",
        }}
      >
        <div className="relative">
          <div className="flex items-center gap-2">
            <GraduationCap size={22} className="text-[#00FA9A]" weight="fill" />
            <div className="eid-label">Markt-Akademie · kostenlos</div>
          </div>
          <h1 className="mt-3 text-3xl md:text-5xl font-medium tracking-tight max-w-3xl">
            Rohstoffmärkte verstehen — ohne BWL-Studium.
          </h1>
          <p className="mt-4 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
            6 kompakte Lektionen, je 2–4 Minuten Lesezeit. Für Bauern, Studierende,
            Schüler:innen und alle, die wissen wollen, warum der Brot-Preis im
            Supermarkt steigt — und was das mit einer Hitzewelle in Argentinien zu tun hat.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="bg-black/60 backdrop-blur-xl px-3 py-1 rounded-full text-xs">📚 6 Lektionen</span>
            <span className="bg-black/60 backdrop-blur-xl px-3 py-1 rounded-full text-xs">⏱️ ~15 Min komplett</span>
            <span className="bg-black/60 backdrop-blur-xl px-3 py-1 rounded-full text-xs">💸 0 € — keine Anmeldung</span>
            <span className="bg-black/60 backdrop-blur-xl px-3 py-1 rounded-full text-xs">🇦🇹🇩🇪 DE/AT-Fokus</span>
          </div>
        </div>
      </div>

      {/* Lesson list */}
      {lessons === null && (
        <div className="eid-card p-6 flex items-center gap-2">
          <CircleNotch size={14} className="animate-spin" /> Lade …
        </div>
      )}
      {lessons && lessons.length > 0 && (
        <div className="space-y-2" data-testid="academy-lessons-list">
          {lessons.map((l) => (
            <LessonCard
              key={l.id}
              lesson={l}
              isOpen={openId === l.id}
              onToggle={() => setOpenId(openId === l.id ? null : l.id)}
            />
          ))}
        </div>
      )}

      {/* Price rhythms timeline */}
      {rhythms.length > 0 && (
        <div className="eid-card p-5" data-testid="academy-rhythms">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={18} className="text-[#00FA9A]" weight="fill" />
            <h2 className="text-base font-medium">Der Preis-Kalender — wann passiert was?</h2>
          </div>
          <p className="text-sm text-zinc-400 mb-4">
            Märkte haben ihren eigenen Wochen- und Jahres-Rhythmus. Hier sind die
            Termine, an denen sich Weizen, Mais & Co. besonders bewegen.
          </p>
          <ol className="space-y-3">
            {rhythms.map((r, i) => (
              <li
                key={i}
                data-testid={`academy-rhythm-${i}`}
                className="grid grid-cols-[24px_1fr] gap-3"
              >
                <div className="font-mono text-[#00FA9A] text-sm">#{i + 1}</div>
                <div>
                  <div className="text-sm font-medium">{r.title}</div>
                  <div className="text-xs text-white mt-0.5">{r.what}</div>
                  <div className="text-xs text-zinc-400 mt-0.5 italic">{r.why}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Cross-link */}
      <div
        className="eid-card p-5"
        style={{ background: "linear-gradient(135deg, #1A160A 0%, #141414 100%)" }}
      >
        <h2 className="text-base font-medium">Theorie war's — jetzt Praxis</h2>
        <p className="text-sm text-zinc-400 mt-1">
          Schau dir die echten Live-Preise auf <a href="/commodities" className="underline text-[#00FA9A]">/commodities</a> an. Dort findest du Weizen, Mais, Raps,
          Sojabohnen mit Charts, Volatilität und EUR-pro-Tonne-Umrechnung —
          alles, was du oben gelernt hast, in Aktion.
        </p>
        <a
          href="/commodities"
          data-testid="academy-cta-commodities"
          className="mt-3 inline-flex items-center gap-1 bg-[#00FA9A] text-black hover:bg-[#33FBAE] rounded-full px-4 py-2 text-sm"
        >
          Live-Preise checken <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
}
